import { FC } from "react";

import { Stack } from "@mui/material";

import type { OrganizationErrorNotice } from "../../helpers/getOrganizationErrorNotice";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

type ExplainedErrorNotice = Exclude<OrganizationErrorNotice, "generic">;

interface ErrorNoticeProps {
  notice: ExplainedErrorNotice;
  onStartNew: () => void;
  onReload: () => void;
}

export const ErrorNotice: FC<ErrorNoticeProps> = ({ notice, onStartNew, onReload }) => {
  const { t } = useTranslate();
  const content: Record<ExplainedErrorNotice, { message: string; action?: { label: string; onClick: () => void } }> = {
    rateLimited: { message: t("dashboard", "organizationAssistant.errors.rateLimited") },
    rateLimitedAfterCards: {
      message: t("dashboard", "organizationAssistant.errors.rateLimited"),
      action: { label: t("dashboard", "assistant.recovery.retry"), onClick: onReload },
    },
    conversationLimit: {
      message: t("dashboard", "assistant.recovery.limit"),
      action: { label: t("dashboard", "assistant.recovery.new"), onClick: onStartNew },
    },
    retryLoading: {
      message: t("dashboard", "assistant.error"),
      action: { label: t("dashboard", "assistant.recovery.retry"), onClick: onReload },
    },
  };
  const { message, action } = content[notice];
  return (
    <Stack spacing={1} alignItems="flex-start" sx={{ pb: 1 }}>
      <DashboardTypography role="alert" variant="body2" vibe="text-danger-intense">
        {message}
      </DashboardTypography>
      {action && (
        <DashboardButton variant="outlined" size="small" onClick={action.onClick}>
          {action.label}
        </DashboardButton>
      )}
    </Stack>
  );
};
